import { useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { supabase } from "@/lib/supabase";
import { clearSessionQueryCache } from "@/lib/queryClient";

const LOGIN_PATH = "/login";

/**
 * Clears TanStack Query and redirects to login when the session ends
 * (explicit logout, expiry, or sign-out in another tab via Supabase sync).
 */
export const AuthQueryCacheSync = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const verifyUserOnServer = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session) {
        const { error } = await supabase.auth.getUser();

        if (error) {
          console.warn(
            "Session invalid or user deleted on server. Forcing logout...",
          );
          await supabase.auth.signOut();
        }
      }
    };

    verifyUserOnServer();

    const handleSessionEnd = () => {
      clearSessionQueryCache();

      if (window.location.pathname !== LOGIN_PATH) {
        navigate(LOGIN_PATH, { replace: true });
      }
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT" || (event === "TOKEN_REFRESHED" && !session)) {
        handleSessionEnd();
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  return null;
};

export const RootLayout = () => (
  <>
    <AuthQueryCacheSync />
    <ErrorBoundary>
      <Outlet />
    </ErrorBoundary>
  </>
);
